import type { PixelPalette, PixelPoint, PixelRun } from '@/lib/pixel'

import {
    createMatrix,
    maskFromRuns,
    matrixToRows,
    paintMask,
    pixelLine,
    seededRandom,
    stamp,
    stampRuns
} from '@/lib/pixel'

/* ------------------------------------------------------------------
   Palettes. Values are CSS colours, so brand tokens flow straight in.
------------------------------------------------------------------ */

export const INK_PALETTE: PixelPalette = {
    K: 'var(--ink)'
}

export const ICON_PALETTE: PixelPalette = {
    K: 'var(--ink)',
    w: '#fffaf2',
    W: '#ffffff',
    G: 'var(--fern)',
    P: 'var(--bubblegum)'
}

export const LOGO_PALETTE: PixelPalette = {
    K: 'var(--ink)',
    G: 'var(--fern)',
    W: '#ffffff',
    P: 'var(--bubblegum)'
}

export const LOGO_PALETTE_DARK: PixelPalette = {
    K: 'var(--paper)',
    G: 'var(--fern)',
    W: 'var(--ink)',
    P: 'var(--bubblegum)'
}

export const DINO_PALETTE: PixelPalette = {
    K: 'var(--ink)',
    G: 'var(--fern)',
    D: 'var(--fern-deep)',
    L: '#7fd97a',
    Y: 'var(--fern-soft)',
    W: '#ffffff',
    P: 'var(--bubblegum)',
    w: '#ececec',
    h: '#9a9a9a'
}

export const CLOUD_PALETTE: PixelPalette = {
    W: 'var(--cloud)',
    S: '#e6e6ee',
    O: '#d2d2db'
}

/* ------------------------------------------------------------------
   The skateboarding dino. Silhouette is drawn as runs, the outline
   and shading are computed, details are stamped on top.
------------------------------------------------------------------ */

export const DINO_WIDTH = 42
export const DINO_HEIGHT = 38
export const DINO_VIEWBOX = `0 2 ${DINO_WIDTH} ${DINO_HEIGHT - 2}`

const DINO_OFFSET = 1

const DINO_BODY_RUNS: ReadonlyArray<PixelRun> = [
    [2, 30, 35],
    [3, 29, 37],
    [4, 28, 38],
    [5, 28, 39],
    [6, 28, 39],
    [7, 28, 38],
    [8, 28, 36],
    [9, 27, 32],
    [10, 27, 32],
    [11, 26, 31],
    [12, 26, 31],
    [13, 17, 21],
    [13, 25, 31],
    [14, 14, 31],
    [15, 12, 31],
    [16, 10, 31],
    [17, 0, 1],
    [17, 9, 32],
    [18, 0, 2],
    [18, 8, 32],
    [19, 1, 4],
    [19, 7, 32],
    [20, 2, 32],
    [21, 3, 32],
    [22, 5, 31],
    [23, 8, 30],
    [24, 11, 15],
    [24, 23, 27],
    [25, 11, 15],
    [25, 23, 27],
    [26, 11, 15],
    [26, 23, 27],
    [27, 10, 15],
    [27, 22, 27],
    [28, 10, 16],
    [28, 22, 28]
]

const DINO_SPOTS: ReadonlyArray<PixelPoint> = [
    [16, 16],
    [17, 16],
    [20, 15],
    [21, 15],
    [13, 18],
    [14, 18]
]

const DINO_MOUTH: ReadonlyArray<PixelPoint> = [
    [32, 6],
    [33, 7],
    [34, 7],
    [35, 7]
]

const DINO_BLUSH: ReadonlyArray<PixelPoint> = [
    [31, 5],
    [32, 5]
]

const DINO_NOSTRIL: ReadonlyArray<PixelPoint> = [
    [38, 5]
]

const buildDinoBody = (): Array<string> => {
    const matrix = createMatrix(DINO_WIDTH, DINO_HEIGHT)
    const mask = maskFromRuns(DINO_BODY_RUNS, DINO_WIDTH, DINO_HEIGHT, DINO_OFFSET)

    paintMask(matrix, mask, {
        fill: 'G',
        top: 'L',
        bottom: 'D',
        outline: 'K',
        skipOutline: (_x, y) => y >= 29 + DINO_OFFSET
    })

    for (let y = 18; y <= 22; y += 1) {
        for (let x = 22; x <= 32; x += 1) {
            const dx = x - 27
            const dy = y - 20

            if (25 * dx * dx + 100 * dy * dy <= 625 && matrix[y + DINO_OFFSET][x + DINO_OFFSET] === 'G') {
                matrix[y + DINO_OFFSET][x + DINO_OFFSET] = 'Y'
            }
        }
    }

    stamp(matrix, DINO_SPOTS, 'D', DINO_OFFSET)
    stamp(matrix, DINO_NOSTRIL, 'D', DINO_OFFSET)
    stamp(matrix, DINO_MOUTH, 'K', DINO_OFFSET)
    stamp(matrix, DINO_BLUSH, 'P', DINO_OFFSET)

    return matrixToRows(matrix)
}

const buildDinoEye = (open: boolean): Array<string> => {
    const matrix = createMatrix(DINO_WIDTH, DINO_HEIGHT)

    if (open) {
        stamp(matrix, [[34, 4], [34, 5], [35, 5]], 'K', DINO_OFFSET)
        stamp(matrix, [[35, 4]], 'W', DINO_OFFSET)
    } else {
        stamp(matrix, [[34, 5], [35, 5]], 'K', DINO_OFFSET)
    }

    return matrixToRows(matrix)
}

const buildDinoBoard = (): Array<string> => {
    const matrix = createMatrix(DINO_WIDTH, DINO_HEIGHT)

    stamp(matrix, [[1, 27], [37, 27], [4, 30], [34, 30]], 'K', DINO_OFFSET)
    stampRuns(matrix, [[28, 1, 3], [28, 35, 37], [29, 3, 35]], 'K', DINO_OFFSET)
    stampRuns(matrix, [[30, 5, 33]], 'P', DINO_OFFSET)
    stampRuns(matrix, [[31, 9, 11], [31, 27, 29]], 'h', DINO_OFFSET)

    return matrixToRows(matrix)
}

export const DINO_BODY = buildDinoBody()
export const DINO_EYE_OPEN = buildDinoEye(true)
export const DINO_EYE_CLOSED = buildDinoEye(false)
export const DINO_BOARD = buildDinoBoard()

export const DINO_WHEEL = [
    '.KKK.',
    'KwhwK',
    'KwKwK',
    'KwwwK',
    '.KKK.'
]

export const DINO_WHEEL_POSITIONS: ReadonlyArray<PixelPoint> = [
    [9, 33],
    [27, 33]
]

/* ------------------------------------------------------------------
   Clouds: white blobs with a soft outline and a two pixel belly shade.
------------------------------------------------------------------ */

const CLOUD_SHAPES: Record<'big' | 'small', { runs: ReadonlyArray<PixelRun>, width: number, height: number }> = {
    big: {
        width: 24,
        height: 8,
        runs: [
            [0, 8, 13],
            [1, 6, 15],
            [2, 5, 16],
            [2, 18, 20],
            [3, 2, 21],
            [4, 1, 22],
            [5, 0, 23],
            [6, 0, 23],
            [7, 1, 22]
        ]
    },
    small: {
        width: 16,
        height: 6,
        runs: [
            [0, 4, 7],
            [1, 2, 9],
            [1, 11, 12],
            [2, 1, 13],
            [3, 0, 15],
            [4, 0, 15],
            [5, 1, 14]
        ]
    }
}

export type CloudShape = keyof typeof CLOUD_SHAPES

const buildCloud = (shape: CloudShape): Array<string> => {
    const { runs, width, height } = CLOUD_SHAPES[shape]
    const matrix = createMatrix(width + 2, height + 2)
    const mask = maskFromRuns(runs, width + 2, height + 2, 1)

    paintMask(matrix, mask, {
        fill: 'W',
        bottom: 'S',
        bottomDepth: 2,
        outline: 'O'
    })

    return matrixToRows(matrix)
}

export const CLOUDS: Record<CloudShape, Array<string>> = {
    big: buildCloud('big'),
    small: buildCloud('small')
}

/* ------------------------------------------------------------------
   Line-art flower: five petals and a centre ring, integer maths only.
------------------------------------------------------------------ */

const FLOWER_HALF = 13

const FLOWER_PETALS: ReadonlyArray<PixelPoint> = [
    [0, -7],
    [7, -2],
    [4, 6],
    [-4, 6],
    [-7, -2]
]

const FLOWER_VEINS: ReadonlyArray<readonly [number, number, number, number]> = [
    [2, -2, 5, -7],
    [3, 1, 9, 3],
    [0, 3, 0, 9],
    [-3, 1, -9, 3],
    [-2, -2, -5, -7]
]

const insideFlower = (dx: number, dy: number): boolean => {
    if (dx * dx + dy * dy <= 16) return true

    return FLOWER_PETALS.some(([px, py]) => (dx - px) * (dx - px) + (dy - py) * (dy - py) <= 30)
}

const buildFlower = (): Array<string> => {
    const size = FLOWER_HALF * 2 + 1
    const matrix = createMatrix(size, size)

    for (let y = 0; y < size; y += 1) {
        for (let x = 0; x < size; x += 1) {
            const dx = x - FLOWER_HALF
            const dy = y - FLOWER_HALF

            if (!insideFlower(dx, dy)) continue

            const edge = !insideFlower(dx + 1, dy) || !insideFlower(dx - 1, dy) || !insideFlower(dx, dy + 1) || !insideFlower(dx, dy - 1)
            const distance = dx * dx + dy * dy

            if (edge) matrix[y][x] = 'O'
            else if (distance >= 4 && distance <= 9) matrix[y][x] = 'C'
        }
    }

    FLOWER_VEINS.forEach(([x0, y0, x1, y1]) => {
        pixelLine(x0, y0, x1, y1).forEach(([dx, dy]) => {
            if (insideFlower(dx, dy) && dx * dx + dy * dy > 9) matrix[dy + FLOWER_HALF][dx + FLOWER_HALF] = 'O'
        })
    })

    return matrixToRows(matrix)
}

export const FLOWER = buildFlower()

/* ------------------------------------------------------------------
   Jagged pixel edge between sections (a deterministic random walk).
------------------------------------------------------------------ */

export const EDGE_COLUMNS = 360
export const EDGE_HEIGHT = 8

export const buildEdgePath = (seed: number): string => {
    const random = seededRandom(seed)
    const heights: Array<number> = []

    let level = 3

    for (let column = 0; column < EDGE_COLUMNS; column += 1) {
        const roll = random()

        if (roll < 0.3) level -= 1
        else if (roll > 0.7) level += 1

        level = Math.max(1, Math.min(EDGE_HEIGHT - 3, level))

        const spike = random() > 0.93 ? 2 + Math.floor(random() * 2) : 0

        heights.push(Math.min(EDGE_HEIGHT, level + spike))
    }

    let d = ''
    let start = 0

    for (let column = 1; column <= EDGE_COLUMNS; column += 1) {
        if (column === EDGE_COLUMNS || heights[column] !== heights[start]) {
            const width = column - start
            const height = heights[start]

            d += `M${start} ${EDGE_HEIGHT - height}h${width}v${height}h-${width}z`
            start = column
        }
    }

    return d
}

/* ------------------------------------------------------------------
   Icons
------------------------------------------------------------------ */

export const LOGO_MARK = [
    '....KKKKK.....',
    '...KGGGGGKK...',
    '...KGGGKWGGK..',
    '...KGGGKKGGGK.',
    '...KGGGGGGGGK.',
    '...KGPGGGKKKK.',
    '...KGGGGK.....',
    '...KGGGGK.....',
    '..KGGGGGK.....',
    '.KGGGGGGK.....',
    'KGGGGGGGK.....',
    'KKKKKKKKK.....'
]

export const ICON_ROBOT = [
    'K...............K',
    '.K.............K.',
    '..K...........K..',
    '..KKKKKKKKKKKKK..',
    '..K...........K..',
    '..K...........K..',
    '..K...........K..',
    '..K...........K..',
    '..K...........K..',
    '..KKKKKKKKKKKKK..'
]

export const ICON_ROBOT_EYES = [
    '.................',
    '.................',
    '.................',
    '.................',
    '.................',
    '.....KK...KK.....',
    '.....KK...KK.....',
    '.................',
    '.................',
    '.................'
]

export const ICON_EYE_OPEN = [
    '.....KKKKKKK.....',
    '...KK.......KK...',
    '.KK....KKK....KK.',
    'K.....KKKKK.....K',
    '.KK....KKK....KK.',
    '...KK.......KK...',
    '.....KKKKKKK.....'
]

export const ICON_EYE_CLOSED = [
    '.................',
    '.................',
    '.................',
    'KK.............KK',
    '..KK.........KK..',
    '....KKKKKKKKK....',
    '.....K..K..K.....'
]

export const ICON_CLOUD = [
    '.......KKKK......',
    '......K....K.....',
    '.....K......K....',
    '..KKK........KK..',
    '.K.............K.',
    'K...............K',
    'K...............K',
    '.KKKKKKKKKKKKKKK.'
]

export const ICON_HEART = [
    '..KKK.....KKK..',
    '.K...K...K...K.',
    'K.....K.K.....K',
    'K......K......K',
    'K.............K',
    '.K...........K.',
    '..K.........K..',
    '...K.......K...',
    '....K.....K....',
    '.....K...K.....',
    '......K.K......',
    '.......K.......'
]

export const ICON_EGG = [
    '....KKK....',
    '...KwwwK...',
    '..KwwGwwK..',
    '.KwwwwwwwK.',
    '.KwGwwwwwK.',
    'KwwwwwwGwwK',
    'KwwwwwwwwwK',
    'KwwGwwwwwwK',
    'KwwwwwwwwwK',
    '.KwwwwwwwK.',
    '..KKKKKKK..'
]

export const ICON_HATCH = [
    '....KKK....',
    '...KwwwK...',
    '..KwwwwwK..',
    '.KwwwwwwwK.',
    '.KwKwwwKwK.',
    'KKKwKwKwKKK',
    'KwwwwKwwwwK',
    'KwwwwwwwwwK',
    'KwwwwwwwwwK',
    '.KwwwwwwwK.',
    '..KKKKKKK..'
]

export const ICON_FOOTPRINT = [
    '.K...K...K.',
    '.K...K...K.',
    '.KK..K..KK.',
    '..K.KKK.K..',
    '..KKKKKKK..',
    '...KKKKK...',
    '...KKKKK...',
    '....KKK....'
]

export const ICON_CROWN = [
    'K....K....K',
    'KK..KPK..KK',
    'KPK.KPK.KPK',
    'KPPKPPPKPPK',
    'KPPPPPPPPPK',
    'KPWPPWPPWPK',
    'KKKKKKKKKKK'
]

export const ICON_SPARKLE = [
    '...K...',
    '...K...',
    '..KKK..',
    'KKKKKKK',
    '..KKK..',
    '...K...',
    '...K...'
]

export const ICON_ARROW = [
    '....K...',
    '....KK..',
    'KKKKKKK.',
    'KKKKKKKK',
    'KKKKKKK.',
    '....KK..',
    '....K...'
]

export const ICON_CHECK = [
    '.......K',
    '......KK',
    'K....KK.',
    'KK..KK..',
    '.KKKK...',
    '..KK....'
]

export const ICON_LOCK = [
    '..KKKKK..',
    '.K.....K.',
    '.K.....K.',
    'KKKKKKKKK',
    'KwwwwwwwK',
    'KwwwKwwwK',
    'KwwwKwwwK',
    'KwwwwwwwK',
    'KKKKKKKKK'
]

export const ICON_PLUS = [
    '..KK..',
    '..KK..',
    'KKKKKK',
    'KKKKKK',
    '..KK..',
    '..KK..'
]

export const ICON_MENU = [
    'KKKKKKK',
    '.......',
    'KKKKKKK',
    '.......',
    'KKKKKKK'
]

export const ICON_WINDOW_MIN = [
    '.....',
    '.....',
    '.....',
    'KKKKK',
    '.....'
]

export const ICON_WINDOW_MAX = [
    'KKKKK',
    'K...K',
    'K...K',
    'K...K',
    'KKKKK'
]

export const ICON_WINDOW_CLOSE = [
    'K...K',
    '.K.K.',
    '..K..',
    '.K.K.',
    'K...K'
]
